import React from 'react';

const fmt = (n) => new Intl.NumberFormat('en-IN', {
  style: 'currency', currency: 'INR', maximumFractionDigits: 0
}).format(n);

const fmtD = (n) => new Intl.NumberFormat('en-IN', {
  style: 'currency', currency: 'INR', maximumFractionDigits: 2
}).format(n);

export { fmt, fmtD };

// ── Field ──────────────────────────────────────────────────
export function Field({ label, value, onChange, placeholder, prefix, suffix, type = 'number', step }) {
  return (
    <div style={{ marginBottom: '16px' }}>
      <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: 'var(--t2)', marginBottom: '7px', letterSpacing: '0.3px', textTransform: 'uppercase' }}>
        {label}
      </label>
      <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
        {prefix && (
          <span style={{ position: 'absolute', left: '14px', color: 'var(--t2)', fontSize: '15px', fontFamily: 'JetBrains Mono, monospace', fontWeight: 600, pointerEvents: 'none', zIndex: 1 }}>
            {prefix}
          </span>
        )}
        <input
          type={type}
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder={placeholder}
          step={step}
          style={{ paddingLeft: prefix ? '32px' : '14px', paddingRight: suffix ? '50px' : '14px' }}
        />
        {suffix && (
          <span style={{ position: 'absolute', right: '14px', color: 'var(--t2)', fontSize: '13px', fontFamily: 'JetBrains Mono, monospace', fontWeight: 500, pointerEvents: 'none' }}>
            {suffix}
          </span>
        )}
      </div>
    </div>
  );
}

// ── Select ─────────────────────────────────────────────────
export function Select({ label, value, onChange, options }) {
  return (
    <div style={{ marginBottom: '16px' }}>
      <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: 'var(--t2)', marginBottom: '7px', letterSpacing: '0.3px', textTransform: 'uppercase' }}>
        {label}
      </label>
      <div style={{ position: 'relative' }}>
        <select value={value} onChange={e => onChange(e.target.value)}>
          {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <span style={{ position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: 'var(--t2)', fontSize: '12px' }}>▼</span>
      </div>
    </div>
  );
}

// ── Card ───────────────────────────────────────────────────
export function Card({ children, style = {} }) {
  return (
    <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '20px', ...style }}>
      {children}
    </div>
  );
}

// ── Calc Button ────────────────────────────────────────────
export function CalcBtn({ label, onClick, color = 'var(--orange)' }) {
  return (
    <button
      onClick={onClick}
      style={{
        width: '100%', height: '52px', border: 'none', borderRadius: '12px', cursor: 'pointer',
        background: `linear-gradient(135deg, ${color}, ${color})`,
        color: 'white', fontSize: '15px', fontWeight: 700, fontFamily: 'Inter, sans-serif',
        letterSpacing: '0.2px', marginTop: '6px',
        boxShadow: `0 6px 20px ${color}55`,
        transition: 'transform 0.15s, box-shadow 0.15s',
      }}
      onMouseEnter={e => { e.target.style.transform = 'translateY(-2px)'; e.target.style.boxShadow = `0 10px 28px ${color}70`; }}
      onMouseLeave={e => { e.target.style.transform = 'translateY(0)'; e.target.style.boxShadow = `0 6px 20px ${color}55`; }}
    >
      {label}
    </button>
  );
}

// ── Hero Result ────────────────────────────────────────────
export function HeroResult({ label, value, color = 'var(--orange)' }) {
  return (
    <div className="fade-up" style={{
      background: `linear-gradient(135deg, ${color}22, ${color}06)`,
      border: `1.5px solid ${color}55`,
      borderRadius: 'var(--radius)', padding: '22px', textAlign: 'center', marginBottom: '14px',
    }}>
      <div style={{ fontSize: '11px', fontWeight: 700, color: `${color}`, letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: '8px' }}>{label}</div>
      <div style={{ fontSize: '34px', fontWeight: 800, color, fontFamily: 'JetBrains Mono, monospace', letterSpacing: '-0.5px' }}>{value}</div>
    </div>
  );
}

// ── Stats Grid ─────────────────────────────────────────────
export function StatsGrid({ chips }) {
  return (
    <div className="fade-up" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '10px', marginBottom: '14px' }}>
      {chips.map((c, i) => (
        <div key={i} style={{
          background: `${c.color}0f`, border: `1px solid ${c.color}30`,
          borderRadius: '12px', padding: '12px',
        }}>
          <div style={{ fontSize: '11px', fontWeight: 600, color: `${c.color}bb`, marginBottom: '5px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.label}</div>
          <div style={{ fontSize: '15px', fontWeight: 800, color: c.color, fontFamily: 'JetBrains Mono, monospace' }}>{c.value}</div>
        </div>
      ))}
    </div>
  );
}

// ── Info Box ───────────────────────────────────────────────
export function InfoBox({ text, color = 'var(--blue)' }) {
  return (
    <div style={{ background: `${color}12`, border: `1px solid ${color}30`, borderRadius: '10px', padding: '12px 14px', marginBottom: '14px', fontSize: '13px', color: `${color}ee`, lineHeight: 1.5 }}>
      {text}
    </div>
  );
}

// ── Table ──────────────────────────────────────────────────
export function DataTable({ headers, rows }) {
  return (
    <div className="fade-up" style={{ overflowX: 'auto', borderRadius: '12px', border: '1px solid var(--border)' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
        <thead>
          <tr style={{ borderBottom: '1px solid var(--border)' }}>
            {headers.map((h, i) => (
              <th key={i} style={{ padding: '10px 14px', textAlign: i === 0 ? 'left' : 'right', color: 'var(--t3)', fontWeight: 700, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, ri) => (
            <tr key={ri} style={{ borderBottom: '1px solid rgba(37,40,57,0.6)' }}>
              {row.map((cell, ci) => (
                <td key={ci} style={{ padding: '10px 14px', textAlign: ci === 0 ? 'left' : 'right', color: cell.color || 'var(--t1)', fontFamily: ci > 0 ? 'JetBrains Mono, monospace' : 'inherit', fontWeight: ci > 0 ? 600 : 400 }}>
                  {cell.value !== undefined ? cell.value : cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
